import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react"
import { CheckIcon, EllipsisVerticalIcon } from "@heroicons/react/24/outline"
import PropTypes from "prop-types"
import React, { Fragment } from "react"
import { twMerge } from "tailwind-merge"

import { DROPDOWN_OPTION_CLASSNAMES } from "./Inputs"
import Typography from "./Typography"

const VARIANT_DEFAULT = "default"
const VARIANT_COMPLETED = "completed"
const VARIANT_SELECTABLE = "selectable"
const VARIANT_SELECTABLE_ACTIVE = "selectable-active"
const VARIANT_SELECTABLE_COMPLETED = "selectable-completed"

const KEBAB_ACTIONS = {
  not_sent: [{ id: "remove", label: "Remove form" }],
  sent: [
    { id: "view", label: "View form" },
    { id: "resend", label: "Resend form" },
    { id: "remove", label: "Remove form" }
  ],
  completed: [
    { id: "view", label: "View form" },
    { id: "remove", label: "Delete response", destructive: true }
  ]
}

function getKebabActions(status) {
  if (status === "completed") return KEBAB_ACTIONS.completed
  if (status === "sent") return KEBAB_ACTIONS.sent
  return KEBAB_ACTIONS.not_sent
}

export default function IntakeFormRow({
  form,
  variant = VARIANT_DEFAULT,
  status = "not_sent",
  completedAt,
  actionIds,
  onAction,
  onToggle,
  className
}) {
  const isSelectable =
    variant === VARIANT_SELECTABLE || variant === VARIANT_SELECTABLE_ACTIVE || variant === VARIANT_SELECTABLE_COMPLETED
  const isChecked = variant === VARIANT_SELECTABLE_ACTIVE
  const isDisabled = variant === VARIANT_SELECTABLE_COMPLETED
  const kebabActions = actionIds
    ? getKebabActions(status).filter((action) => actionIds.includes(action.id))
    : getKebabActions(status)
  const showKebab = !isSelectable && kebabActions.length > 0

  const handleCheckboxChange = () => {
    if (!isDisabled && onToggle) onToggle(form.id)
  }

  const completedAtValue = completedAt ?? form.completedAt
  const formattedDate = completedAtValue
    ? new Date(completedAtValue).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      })
    : null

  return (
    <div
      className={twMerge(
        "flex min-h-[56px] items-center justify-between gap-2 rounded-lg border border-gray-light bg-white px-4 py-3",
        isChecked && "border-gray bg-gray-ultralight",
        className
      )}
      data-testid="intake-form-row">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {isSelectable && (
          <button
            type="button"
            aria-label={isChecked ? "Deselect form" : "Select form"}
            disabled={isDisabled}
            onClick={handleCheckboxChange}
            className={twMerge(
              "flex size-5 shrink-0 items-center justify-center rounded border-2 transition-colors",
              isDisabled && "cursor-not-allowed border-gray bg-gray-ultralight opacity-60",
              isChecked && !isDisabled && "border-teal bg-teal",
              !isChecked && !isDisabled && "border-gray hover:border-teal"
            )}>
            {isChecked && <CheckIcon className="size-3.5 text-white" strokeWidth={3} />}
          </button>
        )}
        <div className="min-w-0 flex-1">
          <Typography variant="body" as="span" className={twMerge("font-semibold", isDisabled && "text-gray-dark")}>
            {form.name}
          </Typography>
          {(variant === VARIANT_COMPLETED || variant === VARIANT_SELECTABLE_COMPLETED) && formattedDate && (
            <div className="mt-0.5">
              <Typography variant="micro" className="text-gray-dark">
                Completed on {formattedDate}
              </Typography>
            </div>
          )}
        </div>
      </div>

      {showKebab && (
        <Menu as="div" className="relative shrink-0">
          <MenuButton
            className="flex size-8 items-center justify-center rounded text-gray-dark hover:bg-gray-ultralight focus:outline-none focus:ring-2 focus:ring-blue"
            aria-label="Form actions">
            <EllipsisVerticalIcon className="size-5" />
          </MenuButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95">
            <MenuItems className="absolute right-0 z-10 mt-1 max-h-72 w-48 origin-top-right overflow-auto rounded-lg border border-gray-light bg-white py-1 shadow-lg focus:outline-none">
              {kebabActions.map((action) => (
                <MenuItem key={action.id}>
                  {({ active }) => (
                    <button
                      type="button"
                      className={twMerge(
                        "block w-full px-3 py-2 text-left text-sm",
                        DROPDOWN_OPTION_CLASSNAMES({ active, selected: false }),
                        action.destructive && "text-red"
                      )}
                      onClick={() => onAction && onAction(action.id, form)}>
                      {action.label}
                    </button>
                  )}
                </MenuItem>
              ))}
            </MenuItems>
          </Transition>
        </Menu>
      )}
    </div>
  )
}

IntakeFormRow.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    completedAt: PropTypes.string
  }).isRequired,
  variant: PropTypes.oneOf([
    VARIANT_DEFAULT,
    VARIANT_COMPLETED,
    VARIANT_SELECTABLE,
    VARIANT_SELECTABLE_ACTIVE,
    VARIANT_SELECTABLE_COMPLETED
  ]),
  status: PropTypes.oneOf(["not_sent", "sent", "completed"]),
  completedAt: PropTypes.string,
  actionIds: PropTypes.arrayOf(PropTypes.oneOf(["view", "resend", "remove"])),
  onAction: PropTypes.func,
  onToggle: PropTypes.func,
  className: PropTypes.string
}

export {
  VARIANT_DEFAULT,
  VARIANT_COMPLETED,
  VARIANT_SELECTABLE,
  VARIANT_SELECTABLE_ACTIVE,
  VARIANT_SELECTABLE_COMPLETED,
  getKebabActions
}
